import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import {
  Button,
  confirm,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ExternalLinkIcon,
  FileTextIcon,
  MegaphoneIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
  UserCogIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type { EventOutsourceDTO } from "../../domain/contracts";
import { performanceApi } from "../api/client";
import {
  useCreateEventOutsource,
  useCreateOutsourcePayout,
  useDeleteEventOutsource,
  useEventOutsourcesQuery,
  useEventPayoutsQuery,
  useUpdateEventOutsource,
} from "../api/queries";
import { EventOutsourceFormDialog } from "./EventOutsourceFormDialog";
import { EventOutsourceInstallmentPlanDialog } from "./EventOutsourceInstallmentPlanDialog";
import { OutsourceBulkFromStaffDialog } from "./OutsourceBulkFromStaffDialog";
import { PerformanceBrandPrintMenu } from "./PerformanceBrandPrintMenu";
import {
  formatOutsourceMoney,
  OutsourceAmountCell,
  OutsourceCategoryBadge,
  OutsourcePayeeCell,
  OutsourcePayeeTypeBadge,
  OutsourceRowStatusBadge,
} from "./outsource-table-cells";

type EventOutsourcesSectionProps = {
  eventId: string;
};

function EventOutsourceRowActions({
  linkedPayout,
  canEdit,
  canDelete,
  canPayout,
  createPayoutPending,
  deletePending,
  onEdit,
  onDelete,
  onCreatePayout,
  onOpenPayout,
  onManagePlan,
  canManagePlan,
}: {
  linkedPayout?: { id: string; payoutNo: string; status: string };
  canEdit: boolean;
  canDelete: boolean;
  canPayout: boolean;
  canManagePlan?: boolean;
  createPayoutPending: boolean;
  deletePending: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCreatePayout: () => void;
  onOpenPayout: (payoutId: string) => void;
  onManagePlan?: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-1.5">
      {canEdit ? (
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          <PencilIcon className="size-4" />
          <span className="hidden sm:inline">ແກ້ໄຂ</span>
        </Button>
      ) : null}
      {canManagePlan && onManagePlan ? (
        <Button type="button" size="sm" variant="outline" onClick={onManagePlan}>
          <span className="hidden sm:inline">ແຜນງວດ</span>
        </Button>
      ) : null}
      {canPayout ? (
        <Button
          type="button"
          size="sm"
          disabled={createPayoutPending}
          onClick={onCreatePayout}
        >
          <FileTextIcon className="size-4" />
          <span className="hidden sm:inline">ສ້າງໃບເບີກຈ່າຍ</span>
        </Button>
      ) : null}
      {linkedPayout && linkedPayout.status !== "cancelled" ? (
        <>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onOpenPayout(linkedPayout.id)}
          >
            <ExternalLinkIcon className="size-4" />
            <span className="hidden sm:inline">{linkedPayout.payoutNo}</span>
          </Button>
          <PerformanceBrandPrintMenu
            className="h-8 gap-1 px-2"
            preview={
              linkedPayout.status === "draft" ||
              linkedPayout.status === "pending_approval"
            }
            buildPrintUrl={(lang) =>
              performanceApi.payouts.printUrl(linkedPayout.id, lang)
            }
          />
        </>
      ) : null}
      {canDelete ? (
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={deletePending}
          onClick={onDelete}
        >
          <Trash2Icon className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

export function EventOutsourcesSection({ eventId }: EventOutsourcesSectionProps) {
  const nav = useNavigate({ from: "/app/performance/events/$id" });
  const canUpdate = useActionPermission(["performance-events:update"]);
  const canCreatePayout = useActionPermission(["performance-payouts:create"]);

  const query = useEventOutsourcesQuery(eventId);
  const payoutsQuery = useEventPayoutsQuery(eventId);
  const createRow = useCreateEventOutsource(eventId);
  const updateRow = useUpdateEventOutsource(eventId);
  const deleteRow = useDeleteEventOutsource(eventId);
  const createPayout = useCreateOutsourcePayout(eventId);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [planRow, setPlanRow] = useState<EventOutsourceDTO | null>(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EventOutsourceDTO | null>(null);

  const items = query.data ?? [];
  const outsourcePayouts = useMemo(
    () => (payoutsQuery.data ?? []).filter((p) => p.isOutsourcePayout),
    [payoutsQuery.data],
  );

  const summary = useMemo(() => {
    let planned = 0;
    let paid = 0;
    for (const row of items) {
      if (row.status === "cancelled") continue;
      if (
        row.paymentMode === "installments" &&
        (row.installments?.length ?? 0) > 0
      ) {
        for (const inst of row.installments!) {
          if (inst.status === "paid") paid += inst.amount;
          else if (inst.status === "pending" || inst.status === "in_payout") {
            planned += inst.amount;
          }
        }
        continue;
      }
      if (row.status === "paid") paid += row.agreedAmount;
      else planned += row.agreedAmount;
    }
    return { planned, paid };
  }, [items]);

  const payoutByRowId = useMemo(() => {
    const map = new Map<string, (typeof outsourcePayouts)[number]>();
    for (const payout of outsourcePayouts) {
      if (payout.eventOutsourceId) {
        map.set(payout.eventOutsourceId, payout);
      }
    }
    return map;
  }, [outsourcePayouts]);

  return (
    <div className="flex h-full min-h-0 flex-col rounded-xl border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <div>
          <div className="flex items-center gap-2">
            <MegaphoneIcon className="size-4 text-primary" />
            <h2 className="font-semibold text-sm uppercase tracking-widest">
              Outsource (KOL/Media)
            </h2>
          </div>
          <p className="mt-1 text-muted-foreground text-xs">
            AP ຕົ້ນທຸນຈິງ — AR ຢູ່ໃນເອກະສານ · Agent ຢູ່ຄ່ານາຍໜ້າ
          </p>
        </div>
        {canUpdate ? (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBulkDialogOpen(true)}
            >
              <UserCogIcon className="size-4" />
              ດຶງ Staff ຫຼາຍຄົນ
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <PlusIcon className="size-4" />
              ເພີ່ມ Outsource
            </Button>
          </div>
        ) : null}
      </div>

      <div className="flex-1 space-y-3 p-5">
        {items.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border bg-muted/20 px-4 py-3">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ວາງແຜນ
              </p>
              <p className="mt-1 font-bold text-lg tabular-nums">
                {formatOutsourceMoney(summary.planned)}
              </p>
            </div>
            <div className="rounded-lg border bg-muted/20 px-4 py-3">
              <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
                ຈ່າຍແລ້ວ
              </p>
              <p className="mt-1 font-bold text-lg tabular-nums">
                {formatOutsourceMoney(summary.paid)}
              </p>
            </div>
          </div>
        ) : null}

        {query.isLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : items.length === 0 ? (
          <Empty className="border border-dashed py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <MegaphoneIcon />
              </EmptyMedia>
              <EmptyTitle>ຍັງບໍ່ມີ Outsource</EmptyTitle>
              <EmptyDescription>
                ລາຄາລູກຄ້າ (AR) ຢູ່ໃນໃບສະເໜີລາຄາ/ໃບເກັບເງິນ — ຕົ້ນທຸນ KOL/Media ບັນທຶກທີ່ນີ້
                (AP) · ຄ່ານາຍໜ້າແຍກຢູ່ຄ່ານາຍໜ້າ
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຜູ້ຮັບ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຫມວດ / ປະເພດ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ຍອດ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ສະຖານະ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-muted-foreground text-xs uppercase tracking-wide">
                    ການກະທຳ
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((row) => {
                  const linkedPayout = row.linkedPayoutId
                    ? outsourcePayouts.find((p) => p.id === row.linkedPayoutId) ??
                      payoutByRowId.get(row.id)
                    : payoutByRowId.get(row.id);
                  const nextPendingInstallment = row.installments?.find(
                    (i) => i.status === "pending",
                  );
                  const hasInPayout = (row.installments ?? []).some(
                    (i) => i.status === "in_payout",
                  );
                  const isLumpSum = (row.paymentMode ?? "lump_sum") === "lump_sum";
                  const canEdit =
                    canUpdate &&
                    row.status === "planned" &&
                    !row.linkedPayoutId &&
                    isLumpSum;
                  const canDelete =
                    canUpdate &&
                    row.status === "planned" &&
                    !linkedPayout &&
                    !hasInPayout;
                  const canPayout =
                    canCreatePayout &&
                    (isLumpSum
                      ? row.status === "planned" && !linkedPayout
                      : (row.status === "planned" ||
                          row.status === "partially_paid") &&
                        !!nextPendingInstallment &&
                        !hasInPayout);
                  const canManagePlan =
                    canUpdate &&
                    row.status !== "cancelled" &&
                    row.status !== "paid";

                  return (
                    <tr key={row.id} className="align-top hover:bg-muted/20">
                      <td className="px-4 py-3">
                        <OutsourcePayeeCell
                          displayName={row.displayName}
                          payeeType={row.payeeType}
                          contactName={row.contactName}
                          roleLabel={row.roleLabel}
                          deliverableSummary={row.deliverableSummary}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col items-start gap-2 py-1">
                          <OutsourceCategoryBadge
                            name={row.categoryName}
                            slug={row.categorySlug}
                          />
                          <OutsourcePayeeTypeBadge payeeType={row.payeeType} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <OutsourceAmountCell amount={row.agreedAmount} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1.5 py-1">
                          <OutsourceRowStatusBadge status={row.status} />
                          {row.paymentMode === "installments" &&
                          (row.installments?.length ?? 0) > 0 ? (
                            <p className="text-muted-foreground text-xs">
                              {(row.installments ?? []).filter(
                                (i) => i.status === "paid",
                              ).length}
                              /{row.installments!.length} ງວດ
                            </p>
                          ) : linkedPayout ? (
                            <p className="text-muted-foreground text-xs">
                              {linkedPayout.payoutNo}
                            </p>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <EventOutsourceRowActions
                          linkedPayout={linkedPayout}
                          canEdit={!!canEdit}
                          canDelete={!!canDelete}
                          canPayout={!!canPayout}
                          canManagePlan={!!canManagePlan}
                          createPayoutPending={createPayout.isPending}
                          deletePending={deleteRow.isPending}
                          onEdit={() => {
                            setEditing(row);
                            setDialogOpen(true);
                          }}
                          onDelete={async () => {
                            const ok = await confirm({
                              title: "ລຶບ Outsource",
                              description: "ລຶບແຖວນີ້ອອກຖາວອນ?",
                              actionText: "ລຶບ",
                              ActionProps: { variant: "destructive" },
                            });
                            if (ok) deleteRow.mutate(row.id);
                          }}
                          onManagePlan={() => {
                            setPlanRow(row);
                            setPlanDialogOpen(true);
                          }}
                          onCreatePayout={async () => {
                            const payoutAmount =
                              nextPendingInstallment?.amount ?? row.agreedAmount;
                            const ok = await confirm({
                              title: "ສ້າງໃບເບີກຈ່າຍ Outsource",
                              description: `ສ້າງໃບເບີກຈ່າຍ ${formatOutsourceMoney(payoutAmount)} ສຳລັບ ${row.displayName}?`,
                              actionText: "ສ້າງ",
                            });
                            if (ok) {
                              createPayout.mutate({
                                rowId: row.id,
                                input: nextPendingInstallment
                                  ? { installmentId: nextPendingInstallment.id }
                                  : {},
                              });
                            }
                          }}
                          onOpenPayout={(payoutId) =>
                            nav({
                              to: "/app/performance/payouts/$id",
                              params: { id: payoutId },
                            })
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <EventOutsourceFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        submitting={createRow.isPending || updateRow.isPending}
        onSubmit={(values) => {
          if (editing) {
            updateRow.mutate(
              { rowId: editing.id, input: values },
              { onSuccess: () => setDialogOpen(false) },
            );
          } else {
            createRow.mutate(values, {
              onSuccess: () => setDialogOpen(false),
            });
          }
        }}
      />

      <EventOutsourceInstallmentPlanDialog
        open={planDialogOpen}
        onOpenChange={setPlanDialogOpen}
        eventId={eventId}
        row={planRow}
      />

      <OutsourceBulkFromStaffDialog
        open={bulkDialogOpen}
        onOpenChange={setBulkDialogOpen}
        mode="event"
        eventId={eventId}
        existingDisplayNames={items
          .filter((row) => row.status !== "cancelled")
          .map((row) => row.displayName)}
      />
    </div>
  );
}
